/**
 * Fun Challenges Lobby route.
 *
 * Reachable from PlayChess → "Fun Challenges" tile in `GameSearch`. Phase 4
 * replaces the Phase 1 placeholder with the real lobby (game-type tile,
 * opponent picker, challenge settings).
 *
 * Side-effect imports `@/Screens/FunChallenges/adapters` so every battle adapter is
 * registered before the user picks a battle type.
 *
 * 2026-05-20 bug 3: if the user lands here while a fun challenge battle
 * is still active (e.g. they navigated away via the side menu and then
 * tapped Fun Challenges again), bounce them back to FunChallengeBattle.
 * The existing header pill ("Challenge in progress") already covers the
 * passive case, but the menu tap should reuse the live battle, not let
 * the user start a second one.
 */

import React, {useEffect} from 'react';
import {View} from 'react-native';

import {useNavigation} from '@react-navigation/native';

import BattleLobby from './components/BattleLobby';
import useFunChallengesBattleStore from '@/Store/funChallengesBattleStore';
import {ScreenName} from '@/navigators/StackConstants';
import {useSocketReconnect} from '@/Hooks/UseSocket';
import ConnectionModal from '@/Screens/PlayChess/components/ConnectionModal';
import ConnectionNotice from './components/ConnectionNotice';

import '@/Screens/FunChallenges/adapters';

export default function FunChallengesLobby(props: {
  // Threaded down from Application.tsx like every drawer screen — drives the
  // shared ConnectionModal so the lobby can reclaim the socket / re-auth.
  changeClient?: boolean;
  setChangeClient?: (val: boolean) => void;
  authComplete?: boolean;
}) {
  const {changeClient, setChangeClient, authComplete = true} = props;
  const navigation = useNavigation<any>();
  const reconnectSocket = useSocketReconnect();
  const status = useFunChallengesBattleStore(s => s.status);
  const battleId = useFunChallengesBattleStore(s => s.session?.battleId);

  useEffect(() => {
    if (status !== 'in_battle' || !battleId) return;
    try {
      navigation.navigate(ScreenName.FunChallengeBattle);
    } catch {
      // Navigation may not be ready in some auth states — non-fatal.
    }
  }, [status, battleId, navigation]);

  const showConnectionModal = !!changeClient || !authComplete;

  return (
    <>
      {/* The lobby route is wrapped in a ScrollView by the navigator (unlike
          FunChallengeBattle / PlayChess, which use a flex View). The modal's
          own `position:absolute; height:100%` collapses inside a scroll
          container, so wrap it in a viewport-fixed layer so it covers the
          screen the same way it does on PlayChess. */}
      {showConnectionModal && (
        <View
          style={
            {
              position: 'fixed' as any,
              top: 0,
              left: 0,
              right: 0,
              bottom: 0,
              zIndex: 1000,
            } as any
          }>
          <ConnectionModal
            visible
            onContinue={() => {
              setChangeClient?.(false);
              reconnectSocket();
            }}
            onClose={() => navigation.navigate('Dashboard')}
            authFail={!authComplete}
          />
        </View>
      )}
      {/* Socket-level disconnect banner (e.g. when the app takes over the
          shared socket). Parity with PlayChess's NotificationBox. */}
      <ConnectionNotice />
      <BattleLobby />
    </>
  );
}
